import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { Capacitor } from '@capacitor/core';
import { preferences } from '@apexx/storage';
import { resolveAppUpdateDisplayVersions } from '@apexx/shell-contract';
import { onMounted, ref } from 'vue';

import { isPlayStoreUsableForUpdates } from './playStoreAvailability';

const LAST_UPDATE_CHECK_KEY = 'apexx.lastUpdateCheckTimestamp';
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

async function rememberUpdateCheckTimestamp(): Promise<void> {
	try {
		await preferences.setItem(LAST_UPDATE_CHECK_KEY, String(Date.now()));
	} catch {
		// Ignore quota / private-mode failures; next launch will retry the check.
	}
}

export function useAppUpdateCheck() {
	const drawerOpen = ref(false);
	const currentVersion = ref<string | undefined>();
	const availableVersion = ref<string | undefined>();
	const currentDisplayVersion = ref<string | undefined>();
	const availableDisplayVersion = ref<string | undefined>();
	const isChecking = ref(false);

	async function checkForUpdate(): Promise<void> {
		if (!Capacitor.isNativePlatform() || isChecking.value) {
			return;
		}

		const lastCheck = Number((await preferences.getItem(LAST_UPDATE_CHECK_KEY)) ?? 0);
		if (Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS) {
			return;
		}

		isChecking.value = true;

		try {
			if (!(await isPlayStoreUsableForUpdates())) {
				await rememberUpdateCheckTimestamp();
				return;
			}

			const info = await AppUpdate.getAppUpdateInfo();
			await rememberUpdateCheckTimestamp();

			if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
				return;
			}

			const resolved = resolveAppUpdateDisplayVersions({
				platform: Capacitor.getPlatform(),
				currentVersionName: info.currentVersionName,
				availableVersionName: info.availableVersionName,
				currentVersionCode: info.currentVersionCode,
				availableVersionCode: info.availableVersionCode,
			});

			if (!resolved.isUpdateAvailable || !resolved.currentDisplay) {
				return;
			}

			currentVersion.value = info.currentVersionName;
			availableVersion.value = info.availableVersionName;
			currentDisplayVersion.value = resolved.currentDisplay;
			availableDisplayVersion.value = resolved.availableDisplay;
			drawerOpen.value = true;
		} catch {
			await rememberUpdateCheckTimestamp();
		} finally {
			isChecking.value = false;
		}
	}

	async function openAppStore(): Promise<void> {
		try {
			const appId = import.meta.env.VITE_IOS_APP_STORE_ID;
			if (Capacitor.getPlatform() === 'ios' && !appId) {
				console.error('[hub] VITE_IOS_APP_STORE_ID is required to open the App Store');
				return;
			}

			await AppUpdate.openAppStore(appId ? { appId } : undefined);
			drawerOpen.value = false;
		} catch (error) {
			console.error('[hub] Failed to open app store', error);
		}
	}

	function dismissUpdate(): void {
		drawerOpen.value = false;
	}

	onMounted(() => {
		void checkForUpdate();
	});

	return {
		drawerOpen,
		currentVersion,
		availableVersion,
		currentDisplayVersion,
		availableDisplayVersion,
		checkForUpdate,
		openAppStore,
		dismissUpdate,
	};
}
